import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type {
    AssessorTaskDetailsResponse,
    AssessorTaskWriteRequest,
} from '@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/api/evaluateTask.api.ts';
import {
    getApiAssessorTask,
    putApiAssessorTask,
} from '@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/api/evaluateTask.api.ts';
import { extractError } from '@utils/slice.ts';

interface AssessorEvaluateTaskError {
    message?: string;
    [key: string]: unknown;
}

interface AssessorEvaluateTaskState {
    error: AssessorEvaluateTaskError | null;
    taskData: AssessorTaskDetailsResponse | null;
    isGetAssessorTaskPending: boolean;
    isUpdateAssessorTaskPending: boolean;
}

const initialState: AssessorEvaluateTaskState = {
    error: null,
    taskData: null,
    isGetAssessorTaskPending: false,
    isUpdateAssessorTaskPending: false,
};

export const getAssessorTask = createAsyncThunk<
    AssessorTaskDetailsResponse,
    { userPathwayId: number; pathwayItemId: number },
    { rejectValue: AssessorEvaluateTaskError }
>('assessorEvaluateTask/getAssessorTask', ({ userPathwayId, pathwayItemId }, { rejectWithValue }) =>
    getApiAssessorTask(userPathwayId, pathwayItemId)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateAssessorTask = createAsyncThunk<
    AssessorTaskDetailsResponse,
    { userPathwayId: number; pathwayItemId: number; payload: AssessorTaskWriteRequest },
    { rejectValue: AssessorEvaluateTaskError }
>('assessorEvaluateTask/updateAssessorTask', ({ userPathwayId, pathwayItemId, payload }, { rejectWithValue }) =>
    putApiAssessorTask(userPathwayId, pathwayItemId, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const assessorEvaluateTaskSlice = createSlice({
    name: 'assessorEvaluateTask',
    initialState,
    reducers: {
        resetAssessorTask: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAssessorTask.pending, (state) => {
                state.isGetAssessorTaskPending = true;
                state.error = null;
            })
            .addCase(getAssessorTask.fulfilled, (state, action) => {
                state.isGetAssessorTaskPending = false;
                state.taskData = action.payload;
            })
            .addCase(getAssessorTask.rejected, (state, action) => {
                state.isGetAssessorTaskPending = false;
                state.error = extractError(action.payload);
            })
            .addCase(updateAssessorTask.pending, (state) => {
                state.isUpdateAssessorTaskPending = true;
                state.error = null;
            })
            .addCase(updateAssessorTask.fulfilled, (state, action) => {
                state.isUpdateAssessorTaskPending = false;
                state.taskData = action.payload;
            })
            .addCase(updateAssessorTask.rejected, (state, action) => {
                state.isUpdateAssessorTaskPending = false;
                state.error = extractError(action.payload);
            });
    },
});

export const { resetAssessorTask } = assessorEvaluateTaskSlice.actions;

export default assessorEvaluateTaskSlice.reducer;
